import { useCallback, useEffect, useState } from "react";
import { Link, useSearch } from "wouter";

import { apiClient } from "../api/client.js";
import { useAuth } from "../auth/AuthContext.js";

type State =
  | { phase: "validating" }
  | { phase: "form"; code: string }
  | { phase: "submitting"; code: string; name: string }
  | { phase: "success"; deviceName: string }
  | { phase: "error"; message: string };

export function SetupDevice() {
  const search = useSearch();
  const params = new URLSearchParams(search);
  const code = params.get("code");

  const { selectedVenue } = useAuth();
  const [state, setState] = useState<State>({ phase: "validating" });
  const [deviceName, setDeviceName] = useState("");

  useEffect(() => {
    if (!code) {
      setState({ phase: "error", message: "No setup code provided" });
      return;
    }

    setState({ phase: "validating" });

    apiClient["/api/v1/venue/setup-codes/{code}"]["get"]({
      parameters: { code },
      body: null,
    })
      .then((res) => {
        if (res.code === 200) {
          setState({ phase: "form", code });
        } else if (res.code === 410) {
          setState({ phase: "error", message: "This setup code has expired or already been used." });
        } else if (res.code === 404) {
          setState({ phase: "error", message: "Setup code not found." });
        } else {
          setState({ phase: "error", message: "Something went wrong." });
        }
      })
      .catch(() => {
        setState({ phase: "error", message: "Network error. Please try again." });
      });
  }, [code]);

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (!code || !deviceName.trim()) return;

      setState({ phase: "submitting", code, name: deviceName });

      try {
        const res = await apiClient["/api/v1/venue/setup-codes/{code}/claim"]["post"]({
          parameters: { code, venue_id: selectedVenue?.id ?? "" },
          body: { name: deviceName.trim() },
        });

        if (res.code === 200) {
          setState({ phase: "success", deviceName: res.body.device_name });
        } else if (res.code === 410) {
          setState({ phase: "error", message: "This setup code has expired or already been used." });
        } else if (res.code === 403) {
          setState({
            phase: "error",
            message: "You don't have permission to add devices.",
          });
        } else {
          setState({ phase: "error", message: "Something went wrong." });
        }
      } catch {
        setState({ phase: "error", message: "Network error. Please try again." });
      }
    },
    [code, deviceName, selectedVenue],
  );

  if (state.phase === "validating") {
    return (
      <main className="p-6">
        <p className="text-text-secondary">Validating setup code...</p>
      </main>
    );
  }

  if (state.phase === "error") {
    return (
      <main className="p-6 max-w-lg">
        <h1 className="text-xl font-bold text-text-primary mb-2">Unable to Set Up Device</h1>
        <p className="text-text-secondary mb-4">{state.message}</p>
        <Link href="/" className="text-sm text-accent hover:underline">
          Back to dashboard
        </Link>
      </main>
    );
  }

  if (state.phase === "success") {
    return (
      <main className="p-6 max-w-lg">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-full bg-green-500/20 flex items-center justify-center">
            <svg
              className="w-5 h-5 text-green-500"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h1 className="text-xl font-bold text-text-primary">Device Added</h1>
        </div>
        <p className="text-text-secondary mb-6">
          <span className="font-medium text-text-primary">{state.deviceName}</span> has been added
          to <span className="font-medium text-text-primary">{selectedVenue?.name}</span>.
        </p>
        <Link
          href="/"
          className="inline-block px-4 py-2 rounded-lg bg-accent text-white text-sm font-medium hover:bg-accent-hover transition-colors"
        >
          Back to dashboard
        </Link>
      </main>
    );
  }

  return (
    <main className="p-6 max-w-lg">
      <h1 className="text-xl font-bold text-text-primary mb-1">Add Device</h1>
      <p className="text-text-secondary text-sm mb-6">
        Adding to <span className="font-medium text-text-primary">{selectedVenue?.name}</span>
      </p>
      <form onSubmit={handleSubmit} className="flex flex-col gap-4">
        <div>
          <label htmlFor="device-name" className="block text-sm text-text-secondary mb-1">
            Device name
          </label>
          <input
            id="device-name"
            type="text"
            value={deviceName}
            onChange={(e) => setDeviceName(e.target.value)}
            placeholder="e.g. Table 5, Bar Left"
            autoFocus
            className="w-full px-4 py-2.5 rounded-lg bg-surface-overlay border border-border text-text-primary placeholder:text-text-muted focus:outline-none focus:border-accent transition-colors"
          />
        </div>
        <button
          type="submit"
          disabled={!deviceName.trim() || state.phase === "submitting"}
          className="w-full px-4 py-2.5 rounded-lg bg-accent text-white text-sm font-medium hover:bg-accent-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {state.phase === "submitting" ? "Adding..." : "Add Device"}
        </button>
      </form>
    </main>
  );
}
